import { useState } from 'react';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useCreateFormStore, useFormContext } from '@components/form';
import { FormInstance } from '@components/form/create-form';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { IconHourglass } from '@components/icons/hourglass';
import { Spinner } from '@components/loading-spinner';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar, hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { uuid } from '@utils/uuid';

import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { ConditionComparator, TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import { Data, filterAttributeVariables } from './helpers';

import {
  ActionProgressBar,
  EditableActionContent,
  FormSidebarFooter,
  isActionError,
  SidebarProps,
  TravellerActionActions,
  useEditingContext,
} from '../common';
import { useConditionComparators } from '../condition-v2/comparators';
import { getComparatorLabel, getValueLabel } from '../condition-v2/description';
import { getFieldMappingId } from '../helpers';
import { ComparatorValueContainer } from '../shared/comparator-component';

type ActionType = 'wait_for_attribute_change';

type WaitForAttributeChangeData = {
  field: VariableValue;
  comparator: ConditionComparator;
  value: any;
};

const Form = createForm<WaitForAttributeChangeData>();

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }

  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('waitForAttributeChange')}
        icon={IconHourglass}
        iconColor="orange100"
      >
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const { startEditing } = useEditingContext();

  const field = action.values?.field;
  const comparator = action.values?.comparator as ConditionComparator;
  const value = action.values?.value;
  const triggeredValue = action.savedValues?.triggered_value;

  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  const fieldLabel = field?.variables && Object.values(field.variables)[0]?.label;
  const comparatorLabel = comparator
    ? comparator === 'changes_value'
      ? t('changesValue')
      : getComparatorLabel(comparator)
    : t('unknownCondition');
  const valueLabel = getValueLabel(value, comparator);

  const isConfigured = Boolean(fieldLabel && comparator);

  const translationKey =
    comparator?.includes('empty') || comparator === 'changes_value'
      ? 'conditionInstanceWithoutValue'
      : 'conditionInstance';

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionProgressBar
          action={action}
          progressText={progressText}
          isError={isError}
          dateSuccess={action.result?.dateSucceeded}
          isCard={false}
          progressPercentage=" "
        />
      </HideContainer>
      <Separator />
      <HideContainer title={t('fieldCondition')}>
        {isConfigured ? (
          <Stack space={4}>
            <Text font="regular14" text={t('continueWhen')} />
            <Text
              font="semibold14"
              text={styledTranslate({
                key: translationKey,
                variables: {
                  field: {
                    text: fieldLabel,
                    color: 'elementBlue100',
                    font: 'semibold14',
                  },
                  comparator: {
                    text: comparatorLabel,
                    color: 'textPrimary',
                    font: 'semibold14',
                  },
                  value: {
                    text: valueLabel,
                    color: 'textPrimary',
                    font: 'semibold14',
                  },
                },
                translations: sidebarLocale,
              })}
              style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            />
            {status === 'completed' && triggeredValue !== undefined && (
              <Text
                font="regular14"
                text={styledTranslate({
                  key: 'triggeredValueLabel',
                  variables: {
                    value: {
                      text: String(
                        triggeredValue?.label ||
                          triggeredValue?.value ||
                          triggeredValue ||
                          t('none')
                      ),
                      color: 'textPrimary',
                      font: 'regular14',
                    },
                  },
                  translations: sidebarLocale,
                })}
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              />
            )}
          </Stack>
        ) : (
          <Text font="regular14" text={t('notConfigured')} />
        )}
      </HideContainer>
      <Separator />
      {status !== 'completed' && (
        <Sidebar.Footer>
          <Stack orientation="horizontal" justify="space-between">
            <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
            <Button text={t('core.close')} variant="secondary" onClick={hideSidebar} />
          </Stack>
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();

  const journeyId = action.journeyId;
  const fieldMappingId = getFieldMappingId(journeyId);

  const baseVariableGroups = useVariableGroups({
    actionVariables: action?.actionVariables,
    journeyId,
    types: [
      'text',
      'paragraph',
      'phone',
      'date',
      'boolean',
      'file',
      'select',
      'multi_select',
      'number',
      'decimal',
    ],
  });

  // Filter to only show attribute variables
  const attributeVariableGroups = filterAttributeVariables(baseVariableGroups);

  const initialCondition: ConditionState = {
    id: uuid(),
    field: action.values?.field ?? ({ value: '' } as VariableValue),
    comparator: action.values?.comparator as ConditionComparator | undefined,
    value: action.values?.value,
  };

  const [condition, setCondition] = useState(initialCondition);
  const isDirty = formStore.useIsDirty();

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const formValues = formStore.useFormValues();

  const handleSubmit = () => {
    const field = formValues.field;
    const comparator = formValues.comparator;
    const value = formValues.value;

    if (!field) {
      throw new Error('No attribute selected');
    }

    const submissionData = {
      field,
      comparator,
      value,
    } as Data;

    return editMutation.mutateAsync({
      values: submissionData,
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <Stack space={4}>
          <Text text={t('actionDescription')} font="regular14" />
        </Stack>
        <Separator />

        <Stack space={4}>
          <Text font="semibold14" text={t('attributeCondition')} />
          <SingleConditionComponent
            variableGroups={attributeVariableGroups}
            condition={condition}
            setCondition={setCondition}
            form={Form}
            fieldMappingId={fieldMappingId}
          />
        </Stack>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

type ConditionState = {
  id: string;
  field: VariableValue;
  comparator?: ConditionComparator;
  value?: any;
};

type SingleConditionComponentProps = {
  variableGroups: FieldMappingGroup[];
  condition: ConditionState;
  setCondition: React.Dispatch<React.SetStateAction<ConditionState>>;
  form: FormInstance<WaitForAttributeChangeData>;
  fieldMappingId: string;
};

const SingleConditionComponent = ({
  variableGroups,
  condition,
  setCondition,
  form: Form,
  fieldMappingId,
}: SingleConditionComponentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { store } = useFormContext();
  const element = store.useFieldValue('field') as VariableValue;

  const handleChangeFieldMapping = () => {
    setCondition({
      ...condition,
      comparator: undefined,
      value: undefined,
      field: { value: '' },
    });
  };

  return (
    <Card space={2}>
      <Stack space={2}>
        <Form.FieldMappingField
          id="field"
          label={t('attributeLabel')}
          variableGroups={variableGroups}
          defaultValue={condition.field}
          validators={['required']}
          singleLine
          plaintext
          onChange={handleChangeFieldMapping}
          fieldMappingId={fieldMappingId}
        />
        {(element?.value || condition.field?.value) && (
          <Comparator
            key={element?.value || condition.field?.value || 'comparator'}
            form={Form}
            condition={condition}
            variableGroups={variableGroups}
          />
        )}
      </Stack>
    </Card>
  );
};

const Comparator = ({
  condition,
  form: Form,
  variableGroups,
}: {
  condition: ConditionState;
  form: FormInstance<WaitForAttributeChangeData>;
  variableGroups: FieldMappingGroup[];
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { store } = useFormContext();
  const formValues = store.useFormValues();

  const { getComparator } = useConditionComparators();

  const CHANGES_VALUE_COMPARATOR = {
    value: 'changes_value',
    label: 'Changes value',
  };

  const findMappedField = () => {
    const value = (formValues.field || condition.field) as VariableValue;
    if (!value || !value.variables) {
      return undefined;
    }
    const variable = Object.values(value.variables)[0];
    return variableGroups
      .flatMap((obj) => obj.fields)
      .find((field) => field.source === variable?.source && field.field === variable?.field);
  };

  const mappedField = findMappedField();

  const findValueType = () => {
    const typeMapping: Record<string, string> = {
      select: 'select',
      multi_select: 'multi_select',
      date: 'date',
      boolean: 'boolean',
      file: 'file',
      number: 'number',
      text: 'text',
      decimal: 'number',
    };

    return typeMapping[mappedField?.type ?? 'text'];
  };

  const valueType = findValueType();

  const getComparators = () => {
    const comparatorMap: Record<string, SelectOption[]> = {
      date: [CHANGES_VALUE_COMPARATOR, ...getComparator('date')],
      file: [CHANGES_VALUE_COMPARATOR, ...getComparator('file')],
      boolean: [CHANGES_VALUE_COMPARATOR, ...getComparator('boolean')],
      number: [CHANGES_VALUE_COMPARATOR, ...getComparator('number')],
      select: [CHANGES_VALUE_COMPARATOR, ...getComparator('select')],
      multi_select: [CHANGES_VALUE_COMPARATOR, ...getComparator('multi_select')],
      text: [CHANGES_VALUE_COMPARATOR, ...getComparator()],
    };

    return filterComparatorsForWait(
      comparatorMap[valueType] || [CHANGES_VALUE_COMPARATOR, ...getComparator()]
    );
  };

  const comparators = getComparators();
  const [selectedComparator, setSelectedComparator] = useState<string>(condition.comparator ?? '');

  const allowEmpty = selectedComparator === 'changes_value' || selectedComparator.includes('empty');

  return (
    <ComparatorValueContainer>
      <Form.Select
        id="comparator"
        options={comparators}
        label={t('comparatorLabel')}
        defaultValue={condition.comparator}
        validators={['required']}
        isClearable={false}
        onChange={setSelectedComparator}
      />
      {!allowEmpty &&
        renderValueField(Form, valueType, condition, mappedField, t as (key: string) => string)}
    </ComparatorValueContainer>
  );
};

const renderValueField = (
  Form: FormInstance<WaitForAttributeChangeData>,
  valueType: string,
  condition: ConditionState,
  mappedField: FieldMappingFieldItem | undefined,
  t: (key: string) => string
) => {
  if (valueType === 'date') {
    return (
      <Form.DatePicker
        id="value"
        label={t('dateLabel')}
        validators={['required']}
        defaultValue={condition.value ? new Date(condition.value as string) : undefined}
      />
    );
  }

  if (valueType === 'select') {
    const options =
      mappedField?.options?.map((option) => {
        if ('display' in option) {
          return { value: option.value, label: option.display };
        }
        return { value: option.value, label: option.label ?? option.value };
      }) || [];

    return (
      <Form.Select
        selection="full"
        id="value"
        label={t('valueLabel')}
        options={options}
        defaultValue={condition.value}
        validators={['required']}
        isClearable={false}
      />
    );
  }

  if (valueType === 'multi_select') {
    const options =
      mappedField?.options?.map((option) => {
        if ('display' in option) {
          return { value: option.value, label: option.display };
        }
        return { value: option.value, label: option.label ?? option.value };
      }) || [];

    return (
      <Form.SelectMulti
        id="value"
        label={t('valuesLabel')}
        options={options}
        validators={['required']}
        defaultValue={condition.value}
      />
    );
  }

  if (valueType === 'boolean') {
    // Boolean comparators already encode the value, so no value field needed
    return null;
  }

  if (valueType === 'number') {
    return (
      <Form.TextField
        id="value"
        label={t('valueLabel')}
        validators={['required', 'decimal']}
        defaultValue={condition.value as string}
      />
    );
  }

  return (
    <Form.TextField
      id="value"
      label={t('valueLabel')}
      validators={['required']}
      defaultValue={typeof condition.value === 'string' ? condition.value : ''}
    />
  );
};

const filterComparatorsForWait = (comparators: SelectOption[]): SelectOption[] => {
  const allowedPatterns = ['changes_value', 'empty', 'nempty', 'eq', 'neq', 'contains'];
  return comparators.filter((comp) =>
    allowedPatterns.some((pattern) => (comp.value as string).includes(pattern))
  );
};

const sidebarLocale = {
  en: {
    waitForAttributeChange: 'Wait for field change',
    dataNotFound: 'Data not found',
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    actionStatus: 'Action status',
    fieldCondition: 'Field condition',
    continueWhen: 'Continue when',
    conditionInstance: '{field} • {comparator} • {value}',
    conditionInstanceWithoutValue: '{field} • {comparator}',
    triggeredValueLabel: 'Triggered value • {value}',
    notConfigured: 'Action not configured',
    unknownCondition: 'Unknown condition',
    changesValue: 'changes value',
    actionDescription:
      'This action will wait until the selected field meets the specified condition',
    attributeCondition: 'Field condition',
    attributeLabel: 'Field',
    comparatorLabel: 'Comparator',
    valueLabel: 'Value',
    valuesLabel: 'Values',
    dateLabel: 'Date',
    none: 'None',
  },
};
